import { useEffect, useRef } from 'react';

/**
 * The minimal carousel API surface this hook drives. Structurally satisfied by
 * Embla's `CarouselApi` (`@vs-shop/ui/carousel`) and by any engine exposing the same
 * four methods — keeping `@vs-shop/headless` decoupled from the styled carousel.
 */
export type CarouselSyncApi = {
  selectedScrollSnap(): number;
  scrollTo(index: number): void;
  on(event: 'select', callback: () => void): void;
  off(event: 'select', callback: () => void): void;
};

/**
 * Two-way sync between a carousel API and external `selectedIdx` state.
 * - Carousel-initiated changes (swipe/button) update `selectedIdx` without triggering a redundant `scrollTo`.
 * - External changes (e.g. thumbnail click, keyboard arrows) call `scrollTo` to move the carousel.
 * @param api - Carousel API instance
 * @param selectedIdx - Current selected index
 * @param setSelectedIdx - Setter for selected index
 */
export function useCarouselSync({
  api,
  selectedIdx,
  setSelectedIdx,
}: {
  api: CarouselSyncApi | undefined;
  selectedIdx: number;
  setSelectedIdx: (idx: number) => void;
}) {
  const lastCarouselIdx = useRef<number | null>(null);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const snap = api.selectedScrollSnap();
      lastCarouselIdx.current = snap;
      setSelectedIdx(snap);
    };

    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, setSelectedIdx]);

  useEffect(() => {
    if (!api) return;

    if (selectedIdx === lastCarouselIdx.current) {
      lastCarouselIdx.current = null;
      return;
    }

    api.scrollTo(selectedIdx);
  }, [api, selectedIdx]);
}
